/* ---------------------------------------------------------------------------
   Renderer telemetry.

   The expert surface is inside the trust boundary: nothing here is filtered,
   and an expert is MEANT to see the tool stream, the SHAs and the repo names.
   So this module is not the leak detector its client-dashboard twin is. What it
   catches instead is contract drift — a stream kind, a block type or a status
   this build has no mapping for.

   That still matters, and for a specific reason. The one thing this dashboard
   must never get wrong is which turns the CLIENT can see. If a visibility value
   arrives that the renderer does not recognise, drawing it as though it were
   client-visible would tell the reviewer their hidden work is public, or the
   reverse. So an unmapped value is refused loudly rather than guessed at, and
   `<UnknownTurn>` paints it.

   In development we shout. In production we report and draw a neutral
   placeholder — never the unrecognised payload, because the whole point is that
   we do not know what it is.
--------------------------------------------------------------------------- */

export type IssueKind =
  | 'unknown_stream_event'
  | 'unknown_block_type'
  | 'unknown_author_kind'
  | 'unknown_visibility'
  | 'unknown_reason_code'
  | 'unknown_status'
  | 'unknown_block_code'
  | 'blocked_link_scheme'
  | 'render_error';

export interface IssueReport {
  kind: IssueKind;
  /** A short machine token — never the payload itself. */
  token: string;
  context?: Record<string, string | number | boolean | null | undefined>;
}

type Sink = (report: IssueReport) => void;

const sinks: Sink[] = [];

/** Register an additional sink (Sentry, a beacon endpoint, a test spy). */
export function addTelemetrySink(sink: Sink): () => void {
  sinks.push(sink);
  return () => {
    const i = sinks.indexOf(sink);
    if (i >= 0) sinks.splice(i, 1);
  };
}

const seen = new Set<string>();

export function reportIssue(report: IssueReport): void {
  const dedupeKey = `${report.kind}:${report.token}`;

  if (import.meta.env.DEV) {
    // Loud on purpose. Drift that scrolls past in a debug log is drift that
    // ships.
    // eslint-disable-next-line no-console
    console.error(
      `%c CONTRACT DRIFT %c ${report.kind} → ${report.token}`,
      'background:#a32a22;color:#fff;font-weight:700;padding:2px 6px;border-radius:2px',
      'color:#a32a22;font-weight:600',
      report.context ?? {},
    );
  }

  // One report per distinct token per session: a stuck stream would otherwise
  // emit thousands and drown the signal it exists to produce.
  if (seen.has(dedupeKey)) return;
  seen.add(dedupeKey);

  for (const sink of sinks) {
    try {
      sink(report);
    } catch {
      /* a broken sink must never break a render */
    }
  }
}

/** Test/debug helper. Not used by the app. */
export function resetTelemetryDedupe(): void {
  seen.clear();
}
